import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Input,
  Label,
  FormGroup,
  Alert,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { matchesSearch } from "../../../helpers/search";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

/* DEGER kolonu iki formatta kullanılır:
 *   - Seçenek (A, B, C)   → chip seçim
 *   - Örn: Massey Ferguson → placeholder text
 */
const detectDegerType = (deger) => {
  if (!deger) return { type: "text", options: [] };
  const m = deger.match(/^Seçenek\s*\(([^)]+)\)/i);
  if (m) {
    return {
      type: "options",
      options: m[1].split(",").map((s) => s.trim()).filter(Boolean),
    };
  }
  return { type: "text", options: [] };
};

const KategoriOzellikYonetimi = () => {
  document.title = "Kategori Özellik Yönetimi | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  // ─── DATA ──────────────────────────────────────────────────────────
  const [kategoriler, setKategoriler] = useState([]);
  const [ozellikAdlari, setOzellikAdlari] = useState([]);
  const [haritalar, setHaritalar] = useState([]); // KATEGORI_OZELLIK_DEGERLERI
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  // ─── UI ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("haritalar");
  const [selectedKategoriId, setSelectedKategoriId] = useState("");
  const [search, setSearch] = useState("");

  // ─── HARITALAR (kategori-özellik) MODAL ────────────────────────────
  const [haritaModal, setHaritaModal] = useState(false);
  const [editingHarita, setEditingHarita] = useState(null);
  const [haritaForm, setHaritaForm] = useState({
    KATEGORI_ID: "",
    OZELLIK_AD_ID: "",
    DEGER_TIP: "text", // 'text' | 'options'
    DEGER_TEXT: "",
    DEGER_OPTIONS: "",
  });
  const [isSavingHarita, setIsSavingHarita] = useState(false);

  // ─── OZELLIK_ADLARI MODAL ─────────────────────────────────────────
  const [ozellikModal, setOzellikModal] = useState(false);
  const [editingOzellik, setEditingOzellik] = useState(null);
  const [ozellikForm, setOzellikForm] = useState({ AD: "" });
  const [isSavingOzellik, setIsSavingOzellik] = useState(false);

  // ────────────────────────────────────────────────────────────────────

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3500);
  };

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [kRes, oRes, mRes] = await Promise.all([
        fetch(`${BASE_URL}/KATEGORILER?$orderby=AD asc`, { headers }),
        fetch(`${BASE_URL}/OZELLIK_ADLARI?$orderby=AD asc`, { headers }),
        fetch(
          `${BASE_URL}/KATEGORI_OZELLIK_DEGERLERI?$expand=KATEGORILER,OZELLIK_ADLARI`,
          { headers }
        ),
      ]);

      // OZELLIK_ADLARI / KATEGORI_OZELLIK_DEGERLERI endpoint'leri eski API deploy'da olmayabilir.
      // 404 durumunu ayrı raporla, sayfa diğer veriyi yine de göstersin.
      if (oRes.status === 404 || mRes.status === 404) {
        setError(
          "Sunucudaki API güncel değil: OZELLIK_ADLARI / KATEGORI_OZELLIK_DEGERLERI endpoint'leri eksik. " +
          "Backend'in yeni sürümünü publish edin."
        );
      }

      const safeJson = async (res) => (res.ok ? res.json().catch(() => null) : null);
      const [kJson, oJson, mJson] = await Promise.all([
        safeJson(kRes),
        safeJson(oRes),
        safeJson(mRes),
      ]);
      setKategoriler(kJson?.value || []);
      setOzellikAdlari(oJson?.value || []);
      setHaritalar(mJson?.value || []);
    } catch (e) {
      setError("Veri çekilirken bir hata oluştu: " + e.message);
    } finally {
      setIsLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ─── HARITA CRUD ───────────────────────────────────────────────────

  const openNewHarita = () => {
    setEditingHarita(null);
    setHaritaForm({
      KATEGORI_ID: selectedKategoriId || "",
      OZELLIK_AD_ID: "",
      DEGER_TIP: "text",
      DEGER_TEXT: "Örn: ",
      DEGER_OPTIONS: "",
    });
    setHaritaModal(true);
  };

  const openEditHarita = (h) => {
    const detected = detectDegerType(h.DEGER);
    setEditingHarita(h);
    setHaritaForm({
      KATEGORI_ID: String(h.KATEGORI_ID || ""),
      OZELLIK_AD_ID: String(h.OZELLIK_AD_ID || ""),
      DEGER_TIP: detected.type,
      DEGER_TEXT: detected.type === "text" ? h.DEGER || "" : "",
      DEGER_OPTIONS:
        detected.type === "options" ? detected.options.join(", ") : "",
    });
    setHaritaModal(true);
  };

  const buildDegerString = () => {
    if (haritaForm.DEGER_TIP === "options") {
      const opts = haritaForm.DEGER_OPTIONS
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (opts.length === 0) return "";
      return `Seçenek (${opts.join(", ")})`;
    }
    return haritaForm.DEGER_TEXT.trim();
  };

  const saveHarita = async () => {
    if (!haritaForm.KATEGORI_ID || !haritaForm.OZELLIK_AD_ID) {
      setError("Kategori ve Özellik seçimi zorunludur.");
      return;
    }
    const deger = buildDegerString();
    if (!deger) {
      setError("Değer (placeholder veya seçenek listesi) boş olamaz.");
      return;
    }
    setError(null);

    const payload = {
      KATEGORI_ID: parseInt(haritaForm.KATEGORI_ID),
      OZELLIK_AD_ID: parseInt(haritaForm.OZELLIK_AD_ID),
      DEGER: deger,
    };

    setIsSavingHarita(true);
    try {
      const url = editingHarita
        ? `${BASE_URL}/KATEGORI_OZELLIK_DEGERLERI(${editingHarita.ID})`
        : `${BASE_URL}/KATEGORI_OZELLIK_DEGERLERI`;
      const method = editingHarita ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        let msg = "Kayıt başarısız.";
        try {
          const j = JSON.parse(txt);
          msg = j?.message || j?.error?.message || msg;
        } catch {
          if (txt) msg = txt;
        }
        setError(msg);
        return;
      }
      showSuccess(editingHarita ? "Eşleşme güncellendi." : "Eşleşme eklendi.");
      setHaritaModal(false);
      fetchAll();
    } catch (e) {
      setError("Sunucu hatası: " + e.message);
    } finally {
      setIsSavingHarita(false);
    }
  };

  const deleteHarita = async (h) => {
    if (!window.confirm(`"${h.OZELLIK_ADLARI?.AD}" eşleşmesini silmek istiyor musunuz?`)) return;
    try {
      const res = await fetch(
        `${BASE_URL}/KATEGORI_OZELLIK_DEGERLERI(${h.ID})`,
        { method: "DELETE", headers }
      );
      if (!res.ok) throw new Error("Silme başarısız.");
      showSuccess("Eşleşme silindi.");
      fetchAll();
    } catch (e) {
      setError(e.message);
    }
  };

  // ─── OZELLIK_ADLARI CRUD ───────────────────────────────────────────

  const openNewOzellik = () => {
    setEditingOzellik(null);
    setOzellikForm({ AD: "" });
    setOzellikModal(true);
  };

  const openEditOzellik = (o) => {
    setEditingOzellik(o);
    setOzellikForm({ AD: o.AD || "" });
    setOzellikModal(true);
  };

  const saveOzellik = async () => {
    const ad = ozellikForm.AD.trim();
    if (!ad) {
      setError("Özellik adı zorunlu.");
      return;
    }
    setError(null);
    setIsSavingOzellik(true);
    try {
      const url = editingOzellik
        ? `${BASE_URL}/OZELLIK_ADLARI(${editingOzellik.ID})`
        : `${BASE_URL}/OZELLIK_ADLARI`;
      const method = editingOzellik ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({ AD: ad }),
      });
      if (!res.ok) {
        const txt = await res.text();
        let msg = "Kayıt başarısız.";
        try {
          const j = JSON.parse(txt);
          msg = j?.message || j?.error?.message || msg;
        } catch {
          if (txt) msg = txt;
        }
        setError(msg);
        return;
      }
      showSuccess(editingOzellik ? "Özellik güncellendi." : "Özellik eklendi.");
      setOzellikModal(false);
      fetchAll();
    } catch (e) {
      setError("Sunucu hatası: " + e.message);
    } finally {
      setIsSavingOzellik(false);
    }
  };

  const deleteOzellik = async (o) => {
    if (!window.confirm(`"${o.AD}" özelliğini silmek istiyor musunuz?\n\nNot: kullanılıyorsa silinmez.`)) return;
    try {
      const res = await fetch(`${BASE_URL}/OZELLIK_ADLARI(${o.ID})`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const txt = await res.text();
        let msg = "Silme başarısız.";
        try {
          const j = JSON.parse(txt);
          msg = j?.message || j?.error?.message || msg;
        } catch {
          if (txt) msg = txt;
        }
        setError(msg);
        return;
      }
      showSuccess("Özellik silindi.");
      fetchAll();
    } catch (e) {
      setError(e.message);
    }
  };

  // ─── FILTRELE ──────────────────────────────────────────────────────

  const filteredHaritalar = useMemo(() => {
    let list = haritalar;
    if (selectedKategoriId) {
      list = list.filter((h) => String(h.KATEGORI_ID) === String(selectedKategoriId));
    }
    if (search.trim()) {
      list = list.filter((h) =>
        matchesSearch(h, search, [h.OZELLIK_ADLARI?.AD, h.KATEGORILER?.AD, h.DEGER])
      );
    }
    return list;
  }, [haritalar, selectedKategoriId, search]);

  const filteredOzellikler = useMemo(() => {
    if (!search.trim()) return ozellikAdlari;
    return ozellikAdlari.filter((o) => matchesSearch(o, search, [o.AD, o.ID]));
  }, [ozellikAdlari, search]);

  // ─── RENDER ────────────────────────────────────────────────────────

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Kategori Özellik Yönetimi" />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>
        )}
        {successMsg && <Alert color="success">{successMsg}</Alert>}

        <Row>
          <Col xs="12">
            <Card>
              <CardHeader className="bg-light d-flex flex-wrap align-items-center gap-2">
                <Nav tabs className="border-0 flex-grow-1">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "haritalar" })}
                      onClick={() => setActiveTab("haritalar")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="bx bx-link me-1" />
                      Kategori-Özellik Eşleşmeleri ({filteredHaritalar.length})
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "ozellikler" })}
                      onClick={() => setActiveTab("ozellikler")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="bx bx-list-ul me-1" />
                      Özellik Adları ({filteredOzellikler.length})
                    </NavLink>
                  </NavItem>
                </Nav>
                <Button color="success" size="sm" onClick={fetchAll} disabled={isLoading}>
                  {isLoading ? <Spinner size="sm" /> : <i className="bx bx-refresh" />}
                  {" "}Yenile
                </Button>
              </CardHeader>

              <CardBody>
                <Row className="mb-3">
                  <Col md="4">
                    <Input
                      type="text"
                      placeholder="Ara..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Col>
                  {activeTab === "haritalar" && (
                    <>
                      <Col md="4">
                        <Input
                          type="select"
                          value={selectedKategoriId}
                          onChange={(e) => setSelectedKategoriId(e.target.value)}
                        >
                          <option value="">Tüm Kategoriler</option>
                          {kategoriler.map((k) => (
                            <option key={k.ID} value={k.ID}>{k.AD}</option>
                          ))}
                        </Input>
                      </Col>
                      <Col md="4" className="text-end">
                        <Button color="primary" onClick={openNewHarita}>
                          <i className="bx bx-plus me-1" /> Yeni Eşleşme
                        </Button>
                      </Col>
                    </>
                  )}
                  {activeTab === "ozellikler" && (
                    <Col md="8" className="text-end">
                      <Button color="primary" onClick={openNewOzellik}>
                        <i className="bx bx-plus me-1" /> Yeni Özellik Adı
                      </Button>
                    </Col>
                  )}
                </Row>

                <TabContent activeTab={activeTab}>
                  {/* ─── EŞLEŞMELER TAB ─── */}
                  <TabPane tabId="haritalar">
                    {isLoading ? (
                      <div className="text-center p-5"><Spinner /></div>
                    ) : filteredHaritalar.length === 0 ? (
                      <div className="text-center text-muted p-5">
                        Eşleşme bulunamadı. Sağ üstten "Yeni Eşleşme" ile ekleyebilirsiniz.
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead>
                            <tr>
                              <th>Kategori</th>
                              <th>Özellik</th>
                              <th>Tip</th>
                              <th>Değer / Seçenekler</th>
                              <th className="text-end">İşlemler</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredHaritalar.map((h) => {
                              const det = detectDegerType(h.DEGER);
                              return (
                                <tr key={h.ID}>
                                  <td>{h.KATEGORILER?.AD || `#${h.KATEGORI_ID}`}</td>
                                  <td><strong>{h.OZELLIK_ADLARI?.AD || `#${h.OZELLIK_AD_ID}`}</strong></td>
                                  <td>
                                    {det.type === "options"
                                      ? <Badge color="info">Seçenek ({det.options.length})</Badge>
                                      : <Badge color="secondary">Metin</Badge>}
                                  </td>
                                  <td style={{ maxWidth: 400 }}>
                                    <small>{h.DEGER}</small>
                                  </td>
                                  <td className="text-end">
                                    <Button size="sm" color="info" outline onClick={() => openEditHarita(h)}>
                                      <i className="bx bx-edit" />
                                    </Button>{" "}
                                    <Button size="sm" color="danger" outline onClick={() => deleteHarita(h)}>
                                      <i className="bx bx-trash" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </TabPane>

                  {/* ─── OZELLIK ADLARI TAB ─── */}
                  <TabPane tabId="ozellikler">
                    {isLoading ? (
                      <div className="text-center p-5"><Spinner /></div>
                    ) : filteredOzellikler.length === 0 ? (
                      <div className="text-center text-muted p-5">Özellik adı bulunamadı.</div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: 80 }}>ID</th>
                              <th>Ad</th>
                              <th>Kullanım</th>
                              <th className="text-end">İşlemler</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredOzellikler.map((o) => {
                              const usage = haritalar.filter((h) => h.OZELLIK_AD_ID === o.ID).length;
                              return (
                                <tr key={o.ID}>
                                  <td>{o.ID}</td>
                                  <td><strong>{o.AD}</strong></td>
                                  <td>
                                    {usage > 0
                                      ? <Badge color="success">{usage} kategoride</Badge>
                                      : <Badge color="warning">Kullanılmıyor</Badge>}
                                  </td>
                                  <td className="text-end">
                                    <Button size="sm" color="info" outline onClick={() => openEditOzellik(o)}>
                                      <i className="bx bx-edit" />
                                    </Button>{" "}
                                    <Button size="sm" color="danger" outline onClick={() => deleteOzellik(o)} disabled={usage > 0}>
                                      <i className="bx bx-trash" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ─── HARITA EKLE/DÜZENLE MODAL ─── */}
        <Modal isOpen={haritaModal} toggle={() => setHaritaModal(false)} size="lg">
          <ModalHeader toggle={() => setHaritaModal(false)}>
            {editingHarita ? "Eşleşmeyi Düzenle" : "Yeni Kategori-Özellik Eşleşmesi"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Kategori *</Label>
              <Input
                type="select"
                value={haritaForm.KATEGORI_ID}
                onChange={(e) => setHaritaForm({ ...haritaForm, KATEGORI_ID: e.target.value })}
              >
                <option value="">Seçiniz...</option>
                {kategoriler.map((k) => (
                  <option key={k.ID} value={k.ID}>{k.AD}</option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Özellik Adı *</Label>
              <Input
                type="select"
                value={haritaForm.OZELLIK_AD_ID}
                onChange={(e) => setHaritaForm({ ...haritaForm, OZELLIK_AD_ID: e.target.value })}
              >
                <option value="">Seçiniz...</option>
                {ozellikAdlari.map((o) => (
                  <option key={o.ID} value={o.ID}>{o.AD}</option>
                ))}
              </Input>
              <small className="text-muted">
                Listede yoksa "Özellik Adları" sekmesinden ekleyin.
              </small>
            </FormGroup>

            <FormGroup>
              <Label>Değer Tipi *</Label>
              <div>
                <FormGroup check inline>
                  <Input
                    type="radio"
                    name="degerTip"
                    checked={haritaForm.DEGER_TIP === "text"}
                    onChange={() => setHaritaForm({ ...haritaForm, DEGER_TIP: "text" })}
                  />
                  <Label check>Metin Girişi (Placeholder)</Label>
                </FormGroup>
                <FormGroup check inline>
                  <Input
                    type="radio"
                    name="degerTip"
                    checked={haritaForm.DEGER_TIP === "options"}
                    onChange={() => setHaritaForm({ ...haritaForm, DEGER_TIP: "options" })}
                  />
                  <Label check>Seçenek Listesi (Chip)</Label>
                </FormGroup>
              </div>
            </FormGroup>

            {haritaForm.DEGER_TIP === "text" ? (
              <FormGroup>
                <Label>Placeholder Metni *</Label>
                <Input
                  type="text"
                  placeholder="Örn: Massey Ferguson"
                  value={haritaForm.DEGER_TEXT}
                  onChange={(e) => setHaritaForm({ ...haritaForm, DEGER_TEXT: e.target.value })}
                />
                <small className="text-muted">
                  Kullanıcı bu alan için boş bir input görür; bu metin placeholder olarak gösterilir.
                </small>
              </FormGroup>
            ) : (
              <FormGroup>
                <Label>Seçenekler (virgülle ayır) *</Label>
                <Input
                  type="textarea"
                  rows={3}
                  placeholder="4WD, 2WD, RC, Diğer"
                  value={haritaForm.DEGER_OPTIONS}
                  onChange={(e) => setHaritaForm({ ...haritaForm, DEGER_OPTIONS: e.target.value })}
                />
                <small className="text-muted">
                  Her seçenek virgülle ayrılır. Otomatik olarak "Seçenek (...)" formatına dönüştürülür.
                </small>
                {haritaForm.DEGER_OPTIONS.trim() && (
                  <div className="mt-2">
                    <small>
                      <strong>Önizleme:</strong> {buildDegerString()}
                    </small>
                  </div>
                )}
              </FormGroup>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setHaritaModal(false)}>İptal</Button>
            <Button color="primary" onClick={saveHarita} disabled={isSavingHarita}>
              {isSavingHarita ? <Spinner size="sm" /> : "Kaydet"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ─── OZELLIK ADI EKLE/DÜZENLE MODAL ─── */}
        <Modal isOpen={ozellikModal} toggle={() => setOzellikModal(false)}>
          <ModalHeader toggle={() => setOzellikModal(false)}>
            {editingOzellik ? "Özellik Adını Düzenle" : "Yeni Özellik Adı"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Ad *</Label>
              <Input
                type="text"
                placeholder="Örn: Motor Hacmi"
                value={ozellikForm.AD}
                onChange={(e) => setOzellikForm({ AD: e.target.value })}
              />
              <small className="text-muted">
                Yeni eklenen özellik adı, "Eşleşmeler" sekmesinden istediğiniz kategorilere bağlanabilir.
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setOzellikModal(false)}>İptal</Button>
            <Button color="primary" onClick={saveOzellik} disabled={isSavingOzellik}>
              {isSavingOzellik ? <Spinner size="sm" /> : "Kaydet"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default KategoriOzellikYonetimi;
